import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from './auth/AuthContext'
import { AppShell } from './components/AppShell'
import { LoadingState } from './components/ui'
import { DashboardPage } from './pages/DashboardPage'
import { AnalyticsPage } from './pages/AnalyticsPage'
import { RegistrationPage, RecoverAccessPage } from './pages/AccessPages'
import { AdminUsersPage } from './pages/AdminUsersPage'
import { DevicesPage } from './pages/DevicesPage'
import { AuditLogPage } from './pages/AuditLogPage'
import { CalendarPage } from './pages/CalendarPage'
import { CatalogPage } from './pages/CatalogPage'
import { DefectDetailPage, DefectsPage } from './pages/DefectsPage'
import { EquipmentDetailPage, EquipmentPage } from './pages/EquipmentPage'
import { ImportDetailPage } from './pages/ImportDetailPage'
import { ImportsPage } from './pages/ImportsPage'
import { LoginPage } from './pages/LoginPage'
import { MyWorkPage } from './pages/MyWorkPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { PlanDetailPage } from './pages/PlanDetailPage'
import { PlansPage } from './pages/PlansPage'
import { ProgramsPage } from './pages/ProgramsPage'
import { ReportsPage } from './pages/ReportsPage'
import { ScanPage } from './pages/ScanPage'
import { SyncPage } from './pages/SyncPage'
import { WorkOrderDetailPage } from './pages/WorkOrderDetailPage'

function ProtectedLayout() {
  const { user, ready } = useAuth()
  const location = useLocation()
  if (!ready) return <div className="boot-screen"><LoadingState label="Открываем приложение" /></div>
  if (!user) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  return <AppShell key={user.id} />
}

/** Планирование и контроль: инженер, менеджер, руководитель, администратор. */
function PlannerAccess({ children }: { children: ReactNode }) {
  const { hasAnyRole } = useAuth()
  return hasAnyRole('ROLE_PLANNER') ? children : <Navigate to="/" replace />
}

/** Ведение данных: инженер, администратор. */
function DataEditorAccess({ children }: { children: ReactNode }) {
  const { hasAnyRole } = useAuth()
  return hasAnyRole('ROLE_DATA_EDITOR') ? children : <Navigate to="/" replace />
}

/** Учётные записи исполнителей: менеджер, руководитель, администратор (администратор — все сотрудники и роли). */
function TeamAccess({ children }: { children: ReactNode }) {
  const { hasAnyRole } = useAuth()
  return hasAnyRole('ROLE_TEAM_MANAGER') ? children : <Navigate to="/" replace />
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegistrationPage />} />
      <Route path="/recover-access" element={<RecoverAccessPage />} />
      <Route path="/demo/*" element={<Navigate to="/login" replace />} />
      <Route element={<ProtectedLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="analytics" element={<AnalyticsAccess><AnalyticsPage /></AnalyticsAccess>} />
        <Route path="work-orders" element={<MyWorkPage />} />
        <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
        <Route path="scan" element={<ScanPage />} />
        <Route path="calendar" element={<PlannerAccess><CalendarPage /></PlannerAccess>} />
        <Route path="plans" element={<PlannerAccess><PlansPage /></PlannerAccess>} />
        <Route path="plans/:id" element={<PlannerAccess><PlanDetailPage /></PlannerAccess>} />
        <Route path="defects" element={<DefectsPage />} />
        <Route path="defects/:id" element={<DefectDetailPage />} />
        <Route path="reports" element={<PlannerAccess><ReportsPage /></PlannerAccess>} />
        <Route path="imports" element={<PlannerAccess><ImportsPage /></PlannerAccess>} />
        <Route path="imports/:id" element={<PlannerAccess><ImportDetailPage /></PlannerAccess>} />
        <Route path="equipment" element={<EquipmentPage />} />
        <Route path="equipment/:id" element={<EquipmentDetailPage />} />
        <Route path="programs" element={<PlannerAccess><ProgramsPage /></PlannerAccess>} />
        <Route path="catalog" element={<DataEditorAccess><CatalogPage /></DataEditorAccess>} />
        <Route path="audit-log" element={<PlannerAccess><AuditLogPage /></PlannerAccess>} />
        <Route path="admin/users" element={<TeamAccess><AdminUsersPage /></TeamAccess>} />
        <Route path="devices" element={<TeamAccess><DevicesPage /></TeamAccess>} />
        <Route path="sync" element={<SyncPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}

function AnalyticsAccess({ children }: { children: ReactNode }) {
  const { hasAnyRole } = useAuth()
  return hasAnyRole('ROLE_ADMIN', 'ROLE_SUPERVISOR') ? children : <Navigate to="/" replace />
}
